import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authApi } from "../api/auth";
import { AUTH_EVENT, debugApi } from "../api/client";
import { useToast } from "../lib/toast";
import * as debugCapture from "../lib/debugCapture";

/**
 * Wraps the authenticated app shell. Probes /api/auth/me on mount and
 * redirects to /login on any 401 — both the initial probe and any 401
 * surfaced later by authedFetch (AUTH_EVENT), e.g. a session expiring
 * mid-use.
 */
export default function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<"checking" | "authed">("checking");
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then(() => {
        if (!cancelled) setStatus("authed");
        // Phase D5 — activated once per session, right after confirming
        // login; never guessed client-side. A failed probe (e.g. capture
        // routes disabled/unreachable) just leaves capture off.
        debugApi
          .status()
          .then((s) => debugCapture.setEnabled(s.capture_enabled))
          .catch(() => {});
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true, state: { from: location.pathname } });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      // Only surface this for an already-authed session going stale mid-use
      // — the initial /me probe failing (status still "checking") is the
      // ordinary logged-out case and needs no toast.
      if (status === "authed") toast.error("Session expired — please log in again.");
      navigate("/login", { replace: true });
    };
    window.addEventListener(AUTH_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_EVENT, onUnauthorized);
  }, [navigate, status, toast]);

  if (status === "checking") return null;
  return <>{children}</>;
}
